import { useEffect, useMemo, useState, type FormEvent } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { ApiError, errorMessage } from "../../../api/client";
import { shiftApi, venueApi } from "../../../api/resources";
import type { BulkCreateResponse, EmployeeOption, ShiftAdmin, ShiftInput, VenueAdmin } from "../../../api/types";
import { Modal } from "../../../components/Overlay";
import { Button, ErrorText, Field, TextInput } from "../../../components/ui";
import { NotifyCrewToggle } from "./CrewResponse";
import { BulkResults } from "./BulkResults";
import { EmployeePicker } from "./EmployeePicker";
import { RoleSelect } from "./RoleSelect";
import { todayIn } from "./shiftUi";

interface Form {
  venue_id: string;
  work_date: string;
  start: string;
  end: string;
  next_day: boolean;
  role: string;
  instructions: string;
  status: "TENTATIVE" | "CONFIRMED";
}

const EMPTY: Form = { venue_id: "", work_date: "", start: "", end: "", next_day: false, role: "", instructions: "", status: "TENTATIVE" };

function fromShift(s: ShiftAdmin): Form {
  return { venue_id: s.venue_id, work_date: s.work_date, start: s.start_time_local ?? "", end: s.end_time_local ?? "", next_day: s.ends_next_day,
    role: s.role_id ?? "", instructions: s.instructions ?? "", status: s.status === "CONFIRMED" ? "CONFIRMED" : "TENTATIVE" };
}

function validate(f: Form, employees: number, creating: boolean): string | null {
  if (!f.venue_id) return "Choose a venue.";
  if (!f.work_date) return "Choose a date.";
  if (!!f.start !== !!f.end) return "Provide both a start and an end time, or neither.";
  if (f.start && !f.next_day && f.end <= f.start) return "End time must be after start time. Tick 'Ends next day' for overnight shifts.";
  if (f.start && f.next_day && f.end >= f.start) return "An overnight shift must end earlier on the next day than its start time.";
  if (!f.start && f.next_day) return "'Ends next day' needs a start and end time.";
  if (creating && employees === 0) return "Select at least one employee.";
  if (creating && !f.role) return "Select a role.";
  return null;
}

interface Props {
  open: boolean;
  shift: ShiftAdmin | null;
  onClose: () => void;
  onSaved: () => void;
  onOpen: (id: string) => void;
}

export function ShiftFormModal({ open, shift, onClose, onSaved, onOpen }: Props) {
  const [form, setForm] = useState<Form>(EMPTY);
  const [employeeIds, setEmployeeIds] = useState<string[]>([]);
  const [venues, setVenues] = useState<VenueAdmin[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BulkCreateResponse | null>(null);
  const [notify, setNotify] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(shift ? fromShift(shift) : EMPTY);
    setEmployeeIds([]);
    setError(null);
    setResult(null);
    setNotify(false);
    venueApi.list(undefined, "ALL").then(setVenues).catch(() => setVenues([]));
  }, [open, shift]);

  const choices = venues.filter((v) => v.status === "ACTIVE" || v.id === shift?.venue_id);
  const venue = useMemo(() => venues.find((v) => v.id === form.venue_id), [venues, form.venue_id]);
  const past = !!venue && !!form.work_date && form.work_date < todayIn(venue.timezone);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const problem = validate(form, employeeIds.length, !shift);
    setError(problem);
    if (problem) return;
    const body: ShiftInput = { venue_id: form.venue_id, work_date: form.work_date, start_time_local: form.start || null, end_time_local: form.end || null,
      ends_next_day: !!form.start && form.next_day, role_id: form.role || null,
      role: form.role ? null : shift?.role ?? null, instructions: form.instructions.trim() || null, status: form.status, notify_crew: notify };
    setBusy(true);
    try {
      if (shift) {
        await shiftApi.update(shift.id, body);
        toast.success("Shift updated");
        onSaved();
        onClose();
      } else {
        setResult(await shiftApi.create({ ...body, employee_ids: employeeIds }));
        onSaved();
      }
    } catch (err) {
      if (!shift && err instanceof ApiError && err.status === 409 && employeeIds.length === 1) {
        const opts: EmployeeOption[] = await shiftApi.employeeOptions().catch(() => []);
        setResult({ past_date: past, created: 0, flagged: 0, blocked: 1, results: [{ employee_id: employeeIds[0],
          employee_name: opts.find((o) => o.id === employeeIds[0])?.full_name ?? null, outcome: "BLOCKED", code: err.code, message: err.message, shift: null }] });
      } else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={result ? "Assignment results" : shift ? "Edit shift" : "New assignment"} testId="shift-form-modal" wide>
      {result ? (
        <BulkResults result={result} onDone={onClose} onOpen={(id) => { onClose(); onOpen(id); }} />
      ) : (
        <form onSubmit={submit} noValidate className="space-y-5">
          {shift && <p className="text-sm text-zinc-400">Employee: <span data-testid="shift-form-employee" className="text-white">{shift.employee_name}</span></p>}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Venue" htmlFor="shift-venue">
              <select id="shift-venue" value={form.venue_id} onChange={(e) => set("venue_id", e.target.value)} data-testid="shift-venue-select"
                className="h-11 w-full rounded-sm border border-white/15 bg-surface px-3 text-sm text-white focus:border-signal focus:outline-none">
                <option value="">Select a venue…</option>
                {choices.map((v) => <option key={v.id} value={v.id}>{v.name}{v.status === "ARCHIVED" ? " (archived)" : ""}</option>)}
              </select>
              {venue && <p data-testid="shift-venue-timezone" className="text-xs text-zinc-500">Times are local to the venue · {venue.timezone}</p>}
            </Field>
            <Field label="Date (venue local)" htmlFor="shift-date">
              <TextInput id="shift-date" type="date" value={form.work_date} onChange={(e) => set("work_date", e.target.value)} className="[color-scheme:dark]" data-testid="shift-date-input" />
              {past && <p data-testid="shift-past-date-warning" className="flex items-center gap-1.5 text-xs text-warn"><AlertTriangle className="h-3.5 w-3.5" /> This date is in the past at the venue. You can still save it.</p>}
            </Field>
            <Field label="Start time (optional)" htmlFor="shift-start">
              <TextInput id="shift-start" type="time" value={form.start} onChange={(e) => set("start", e.target.value)} className="[color-scheme:dark]" data-testid="shift-start-input" />
            </Field>
            <Field label="End time (optional)" htmlFor="shift-end">
              <TextInput id="shift-end" type="time" value={form.end} onChange={(e) => set("end", e.target.value)} className="[color-scheme:dark]" data-testid="shift-end-input" />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <input type="checkbox" checked={form.next_day} onChange={(e) => set("next_day", e.target.checked)} className="accent-signal" data-testid="shift-ends-next-day-checkbox" />
            Ends next day (overnight shift)
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={shift ? "Role" : "Role (required)"} htmlFor="shift-role">
              <RoleSelect value={form.role} legacy={shift && !shift.role_id ? shift.role : null} onChange={(v) => set("role", v)} />
            </Field>
            <Field label="Status" htmlFor="shift-status">
              <div id="shift-status" className="flex gap-2">
                {(["TENTATIVE", "CONFIRMED"] as const).map((s) => (
                  <button key={s} type="button" onClick={() => set("status", s)} data-testid={`shift-status-option-${s.toLowerCase()}`} aria-pressed={form.status === s}
                    className={`h-11 flex-1 rounded-sm border text-xs font-semibold uppercase tracking-[0.12em] ${form.status === s ? "border-signal bg-[rgba(255,29,0,0.14)] text-white shadow-[0_0_16px_-4px_rgba(255,29,0,0.6)]" : "border-white/15 text-zinc-400 hover:text-white"}`}>
                    {s === "TENTATIVE" ? "Tentative" : "Confirmed"}
                  </button>
                ))}
              </div>
            </Field>
          </div>
          <Field label="Instructions (optional)" htmlFor="shift-instructions">
            <textarea id="shift-instructions" maxLength={2000} rows={3} value={form.instructions} onChange={(e) => set("instructions", e.target.value)} data-testid="shift-instructions-input"
              className="w-full rounded-sm border border-white/15 bg-surface px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:border-signal focus:outline-none" placeholder="Gate, parking, gear, contact…" />
            <p className="text-right text-[11px] text-zinc-600">{form.instructions.length}/2000</p>
          </Field>
          {!shift && (
            <div className="space-y-2">
              <p className="label-caps text-zinc-400">Employees (one or more)</p>
              <EmployeePicker selected={employeeIds} onChange={setEmployeeIds} />
            </div>
          )}
          <NotifyCrewToggle checked={notify} onChange={setNotify} testId="shift-notify-crew-checkbox"
            hint={shift ? "Only sent if the date, time or venue changes. Resets their reply to awaiting." : "Sends each employee one text with the shift. They reply CONFIRM or DECLINE."} />
          <ErrorText message={error} testId="shift-form-error" />
          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} data-testid="shift-form-cancel-button">Cancel</Button>
            <Button type="submit" loading={busy} data-testid="shift-form-submit-button">{shift ? "Save changes" : `Assign ${employeeIds.length || ""}`.trim()}</Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
